<script setup>
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import { fetchPolls, deletePoll } from "./api";
import { formatDate, toDate } from "@/lib/date";

const router = useRouter();
const columns = [
  { key: "title",      label: "Title",      sortable: true },
  { key: "startDate",  label: "Start Date" },
  { key: "endDate",    label: "End Date" },
  { key: "totalVotes", label: "Votes",      sortable: true },
  { key: "actions",   label: "Actions",    align: "end", width: "150px" },
];
const { rows, loading, error, pendingDelete, deleting, load, askDelete, confirmDelete } =
  useResourceList({
    fetch: fetchPolls,
    remove: deletePoll,
    mapRow: (p) => ({
      id: p._id,
      title: p.question ?? p.title ?? "—",
      startDate: formatDate(p.startDate),
      endDate: formatDate(p.endDate),
      totalVotes: p.totalVotes ?? (Array.isArray(p.answers) ? p.answers.reduce((s, a) => s + (a.votes || 0), 0) : 0),
      _sortDate: toDate(p.updatedAt ?? p.startDate)?.getTime() ?? 0,
    }),
    sort: (a, b) => b._sortDate - a._sortDate,
  });
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Manage Polls</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn @click="router.push({ name: 'polls-new' })">Add Poll</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search polls…" empty-text="No polls yet.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="router.push({ name: 'polls-edit', params: { id: row.id } })">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDelete(row)">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete poll?" :message="`Delete &quot;${pendingDelete?.title || 'this poll'}&quot;?`" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />
  </section>
</template>
